import { LngLat } from "mapbox-gl";
import { computed } from "mobx";
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from "react";
import { useParams } from 'react-router-dom';
import { usePipedriveStore } from "../..";
import useCurrentOrFallbackPipedriveUserId from "../../hooks/useCurrentOrFallbackPipedriveUserId";
import useProperty from "../../hooks/useProperty";
import usePropertyAiGrade from "../../hooks/usePropertyAiGrade";
import usePropertyEstimates from "../../hooks/usePropertyEstimates";
import { DealState, DealStatus } from "../../interfaces/IDeal";
import { formatAddress } from "../../utils/addressFormat";
import OnMapSpinner from "../spinners/OnMapSpinner";
import DealCreate from "./DealCreate";

function DealCreateFromProperty() {
    const { longitude, latitude } = useParams();
    const pipedriveStore = usePipedriveStore();
    const coordinates = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const { property, isLoading, fetchProperty } = useProperty(coordinates);
    const pipedriveUserId = useCurrentOrFallbackPipedriveUserId();
    const { aiGrade } = usePropertyAiGrade(property);
    const estimates = usePropertyEstimates(property);
    const initialState = useMemo(() => computed(() => {
        if (!property)
            return null;

        const usableLand = aiGrade?.usableArea ?? null;
        const value = usableLand && estimates.salePricePerAcre
            ? estimates.salePricePerAcre * usableLand
            : null;

        return {
            ...DealState.empty(),
            name: formatAddress({ address: property.address, city: property.city, state: property.state }),
            address: property.address,
            city: property.city,
            county: property.county,
            state: property.state,
            zoning: property.zoning,
            assignedToUserId: pipedriveUserId,
            buildingSize: property.buildingSize,
            landSizeAcres: property.landSize,
            usableLand,
            value,
            dealShapeApns: property.parcelIds,
            parcelId: property.parcelId,
            status: DealStatus.Open,
            dealStageId: pipedriveStore.dealStages.find(s => s.name === "Pursuit")?.id || null,
        };
    }), [property, pipedriveUserId, aiGrade, estimates.salePricePerAcre]).get();

    useEffect(fetchProperty, [coordinates]);

    if (isLoading)
        return <OnMapSpinner />;

    if (!property)
        return null;

    return (
        <DealCreate
            initialState={initialState!}
            latitude={coordinates.lat}
            longitude={coordinates.lng}
            shapes={property.shapes}
        />
    );
}

export default observer(DealCreateFromProperty);
